import { useState, useEffect, useCallback, type ReactNode, type FormEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import {
  listMyDelegations,
  listReceivedDelegations,
  createDelegation,
  revokeDelegation,
  DELEGATION_STATUS_LABELS,
  type Delegation,
} from '../../api/delegationApi.js';
import { DateInput, validateDateSubmission } from '../../../../shared/components/DateInput.js';

type TFn = (key: string, vars?: Record<string, string | number>) => string;

/**
 * The roles a delegation hands over, in words. One role is named; several are
 * counted, because a column cannot carry a list. Falls back to the count of
 * ids only when a role has been renamed out from under the delegation, and
 * never prints a role id — an id tells the reader nothing about what was
 * handed over.
 */
function roleSummary(names: string[], ids: string[], t: TFn): string {
  if (names.length === 1) return names[0];
  const count = names.length || ids.length;
  return count === 0 ? '—' : t('approvals.delegation.roleCount', { count });
}

export function DelegationPage(): ReactNode {
  const { t } = useTranslation();
  // A delegation window is a pair of instants, written here in the tenant's
  // DISPLAY_DATE_FORMAT. `fmtDate` used to live above and spelled out 'en-IN',
  // so this screen wrote its dates its own way whatever the tenant had chosen.
  const { atDay } = useBusinessDay();
  const { token } = useAuth();
  const [mine, setMine] = useState<readonly Delegation[] | null>(null);
  const [received, setReceived] = useState<readonly Delegation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Create form (§4 full DTO).
  const [showCreate, setShowCreate] = useState(false);
  const [delegateUserId, setDelegateUserId] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  // The chosen roles are shown by the label they were picked with, never their id.
  const [roleLabels, setRoleLabels] = useState<Readonly<Record<string, string>>>({});
  const [reason, setReason] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([
        listMyDelegations(token),
        listReceivedDelegations(token),
      ]);
      setMine(m);
      setReceived(r);
      setError(null);
    } catch (e) {
      setMine([]);
      setError((e as Error).message);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const onCreate = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setInfo(null);
      // Both the person and the roles are picked from lists, so the only checks
      // left are the ones a list cannot make: that something was picked at all.
      if (!delegateUserId.trim()) { setError(t('approvals.delegation.err.delegateRequired')); return; }
      if (roleIds.length === 0) { setError(t('approvals.delegation.err.roleRequired')); return; }
      if (!from || !to) { setError(t('approvals.delegation.err.datesRequired')); return; }
      if (new Date(from) >= new Date(to)) { setError(t('approvals.delegation.err.endAfterStart')); return; }
      setBusy(true);
      try {
        await createDelegation(
          {
            delegateUserId: delegateUserId.trim(),
            delegatedRoleIds: roleIds,
            reason: reason.trim() || undefined,
            effectiveFrom: new Date(from).toISOString(),
            effectiveTo: new Date(to).toISOString(),
          },
          token,
        );
        setInfo(t('approvals.delegation.created'));
        setShowCreate(false);
        setDelegateUserId(''); setRoleIds([]); setRoleLabels({}); setReason(''); setFrom(''); setTo('');
        await load();
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [delegateUserId, roleIds, reason, from, to, token, load],
  );

  const onRevoke = useCallback(
    async (delegationId: string) => {
      setError(null);
      setInfo(null);
      setBusy(true);
      try {
        await revokeDelegation(delegationId, token);
        setInfo(t('approvals.delegation.revoked'));
        await load();
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [token, load],
  );

  return (
    <main aria-label="delegate">
      <h1>{t('approvals.delegation.title')}</h1>

      <button type="button" onClick={() => setShowCreate((v) => !v)} data-testid="toggle-create">
        {showCreate ? t('approvals.delegation.cancel') : t('approvals.delegation.new')}
      </button>

      {showCreate ? (
        <form onSubmit={onCreate} aria-label="delegate-form" noValidate onSubmitCapture={validateDateSubmission}>
          <RecordPicker<{ userId: string; displayName: string; employeeCode: string | null }>
            endpoint="/v1/users?limit=200"
            idField="userId"
            label={t('approvals.delegation.f.delegateTo')}
            testId="d-user"
            value={delegateUserId}
            optionLabel={(u) =>
              [u.employeeCode, u.displayName].filter(Boolean).join(' \u2014 ')
            }
            onPick={setDelegateUserId}
            emptyText="No user on file"
          />

          <RecordPicker<{ roleId: string; roleCode: string; roleName: string }>
            endpoint="/v1/roles?limit=200"
            idField="roleId"
            label={t('approvals.delegation.f.roleIds')}
            testId="d-roles"
            value=""
            optionLabel={(r) => `${r.roleCode} \u2014 ${r.roleName}`}
            onPick={(id, label) => {
              if (!id) return;
              setRoleIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
              setRoleLabels((prev) => ({ ...prev, [id]: label }));
            }}
            emptyText="No role on file"
          />
          {roleIds.length > 0 ? (
            <ul aria-label="chosen-roles" style={{ listStyle: 'none', padding: 0, margin: '0 0 16px' }}>
              {roleIds.map((id) => (
                <li key={id} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginRight: 8 }}>
                  <span>{roleLabels[id]}</span>
                  <button
                    type="button"
                    aria-label={`remove-role-${id}`}
                    onClick={() => setRoleIds((prev) => prev.filter((r) => r !== id))}
                  >
                    &times;
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <label htmlFor="d-reason">{t('approvals.delegation.f.reason')}</label>
          <input id="d-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />

          <label htmlFor="d-from">{t('approvals.delegation.f.from')}</label>
          <DateInput id="d-from" type="date" value={from} onValueChange={(nextValue) => setFrom(nextValue)} />

          <label htmlFor="d-to">{t('approvals.delegation.f.to')}</label>
          <DateInput id="d-to" type="date" value={to} onValueChange={(nextValue) => setTo(nextValue)} />

          <button type="submit" data-testid="submit-create" disabled={busy}>
            {busy ? t('approvals.delegation.saving') : t('approvals.delegation.createBtn')}
          </button>
        </form>
      ) : null}

      {info ? <p role="status" data-testid="delegation-info">{info}</p> : null}
      {error ? <p role="alert" data-testid="delegation-error">{error}</p> : null}

      <section aria-label="my-delegations">
        <h2>{t('approvals.delegation.mineTitle')}</h2>
        {!mine ? (
          error ? null : <p>{t('approvals.delegation.loading')}</p>
        ) : mine.length === 0 ? (
          <p>{t('approvals.delegation.mineEmpty')}</p>
        ) : (
          <table aria-label="my-delegations-table">
            <thead>
              <tr>
                <th>{t('approvals.delegation.col.delegate')}</th>
                <th>{t('approvals.delegation.col.roles')}</th>
                <th>{t('approvals.delegation.col.reason')}</th>
                <th>{t('approvals.delegation.col.from')}</th>
                <th>{t('approvals.delegation.col.to')}</th>
                <th>{t('approvals.delegation.col.status')}</th>
                <th>{t('approvals.delegation.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {mine.map((d) => (
                <tr key={d.delegationId} data-testid={`row-${d.delegationId}`}>
                  <td>{d.delegateName ?? '—'}</td>
                  <td>{roleSummary(d.delegatedRoleNames, d.delegatedRoleIds, t)}</td>
                  <td>{d.reason ?? '—'}</td>
                  <td>{atDay(d.effectiveFrom)}</td>
                  <td>{atDay(d.effectiveTo)}</td>
                  <td>{DELEGATION_STATUS_LABELS[d.recordStatusCode] ?? d.recordStatusCode}</td>
                  <td>
                    {d.recordStatusCode === 'ACTIVE' ? (
                      <button
                        type="button"
                        data-testid={`revoke-${d.delegationId}`}
                        onClick={() => onRevoke(d.delegationId)}
                        disabled={busy}
                      >
                        {t('approvals.delegation.revoke')}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section aria-label="received-delegations">
        <h2>{t('approvals.delegation.receivedTitle')}</h2>
        {received.length === 0 ? (
          <p>{t('approvals.delegation.receivedEmpty')}</p>
        ) : (
          <table aria-label="received-delegations-table">
            <thead>
              <tr>
                <th>{t('approvals.delegation.col.fromUser')}</th>
                <th>{t('approvals.delegation.col.roles')}</th>
                <th>{t('approvals.delegation.col.effectiveFrom')}</th>
                <th>{t('approvals.delegation.col.effectiveTo')}</th>
                <th>{t('approvals.delegation.col.status')}</th>
              </tr>
            </thead>
            <tbody>
              {received.map((d) => (
                <tr key={d.delegationId} data-testid={`recv-${d.delegationId}`}>
                  <td>{d.delegatorName ?? '—'}</td>
                  <td>{roleSummary(d.delegatedRoleNames, d.delegatedRoleIds, t)}</td>
                  <td>{atDay(d.effectiveFrom)}</td>
                  <td>{atDay(d.effectiveTo)}</td>
                  <td>{DELEGATION_STATUS_LABELS[d.recordStatusCode] ?? d.recordStatusCode}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}
